import { useCallback, useMemo } from 'react'

import { InFilterDefinition } from '@factorialco/f0-react'
import {
  PaymentsBeneficiaryFilterDocument,
  PaymentsBeneficiaryFilterQuery,
} from 'generated/resources/graphql'

import { useGraphqlDataSource } from 'modules/core/hooks/useGraphqlDataSource'

const extractConnection = (data: PaymentsBeneficiaryFilterQuery) =>
  data.banking.beneficiaryNameOptionsConnection

const extractNodes = (connection: ReturnType<typeof extractConnection>) =>
  connection.edges.map((edge) => edge.node)

type Node = ReturnType<typeof extractNodes>[number]

export const useBeneficiaryFilter = (label: string): InFilterDefinition<string> => {
  const source = useGraphqlDataSource(
    {
      paginationType: 'infinite-scroll',
      fetchPolicy: 'cache-first',
      document: PaymentsBeneficiaryFilterDocument,
      perPage: 25,
      mapData: (data) => {
        const connection = extractConnection(data)
        return extractNodes(connection)
      },
      mapPagination: (data) => {
        const connection = extractConnection(data)
        const pageInfo = connection.pageInfo
        return {
          total: connection.totalCount ?? 0,
          cursor: pageInfo.endCursor ?? '0',
          hasNextPage: !!pageInfo.hasNextPage,
        }
      },
      mapVariables: ({ search }) => ({
        search,
      }),
    },
    []
  )

  const mapOptions = useCallback(
    (option: Node) => ({
      label: option.name,
      value: option.name,
    }),
    []
  )

  return useMemo(
    () => ({
      label,
      type: 'in' as const,
      options: {
        source,
        mapOptions,
      },
    }),
    [label, source, mapOptions]
  )
}
